import React, {FC, useMemo, useRef, useState} from 'react'
import {FormikErrors, FormikTouched, useFormik} from 'formik'
import * as Yup from 'yup'
import {IPublicJobModel} from '../../hr/job/JobModel'
import {IQuestionModel} from '../../hr/question/QuestionModel'
import {applyJob, uploadCv} from '../PublicApi'
import {apiErrorMessage, IPublicCompanyModel, safeExternalUrl} from '../PublicModels'
import {Turnstile, TURNSTILE_SITE_KEY, TurnstileHandle} from '../components/Turnstile'

const CV_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx']
const CV_MAX_SIZE = 10 * 1024 * 1024
const PHONE_PREFIX = '+90'

interface IApplyFormValues {
  firstname: string
  lastname: string
  email: string
  phone: string
  candidateLocation: string
  cv: File | null
  /** questionId → cevap; seçimli sorularda seçenek, Evet/Hayır'da "true"/"false" */
  answers: Record<string, string>
  consentTerms: boolean
  consentPrivacy: boolean
  consentFutureContact: boolean
}

const fileExtension = (file: File) => file.name.split('.').pop()?.toLowerCase() ?? ''

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`

/** İlana tanımlı soruların tipine göre doğrulama kuralları */
const buildAnswerSchema = (questions: IQuestionModel[]) =>
  Yup.object().shape(
    questions.reduce<Record<string, Yup.StringSchema>>((shape, question) => {
      let schema = Yup.string().trim().max(2000, 'En fazla 2000 karakter')
      if (question.inputType === 'NUMBER') {
        schema = schema.matches(/^-?\d+([.,]\d+)?$/, {message: 'Sayı giriniz', excludeEmptyString: true})
      }
      if (question.required) schema = schema.required('Bu alan zorunludur')
      if (question.id) shape[question.id] = schema
      return shape
    }, {})
  )

type Props = {
  job: IPublicJobModel
  company: IPublicCompanyModel
  onSuccess: () => void
}

/** Başvuru formu: kişisel bilgiler, CV, ilana tanımlı sorular ve onaylar */
export const ApplyForm: FC<Props> = ({job, company, onSuccess}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Başvuru kaydı başarısız olursa aynı dosya tekrar yüklenmesin
  const uploadedCvRef = useRef<{file: File; documentId: string} | null>(null)
  const [dragging, setDragging] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRequired = !!TURNSTILE_SITE_KEY

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstname: Yup.string().trim().required('Ad zorunludur').max(100, 'En fazla 100 karakter'),
        lastname: Yup.string().trim().required('Soyad zorunludur').max(100, 'En fazla 100 karakter'),
        email: Yup.string()
          .trim()
          .required('E-posta zorunludur')
          .email('Geçerli bir e-posta adresi giriniz')
          .max(150, 'En fazla 150 karakter'),
        phone: Yup.string()
          .required('Telefon zorunludur')
          .test('phone', 'Geçerli bir telefon numarası giriniz', (value) =>
            /^\d{10}$/.test((value || '').replace(/\s/g, ''))
          ),
        candidateLocation: Yup.string().trim().required('Konum zorunludur').max(150, 'En fazla 150 karakter'),
        cv: Yup.mixed()
          .test('required', 'CV yüklenmesi zorunludur', (file) => !!file)
          .test('type', 'PDF, PNG, JPG/JPEG, DOC veya DOCX yükleyiniz', (file) =>
            file ? CV_EXTENSIONS.includes(fileExtension(file as File)) : true
          )
          .test('size', 'Dosya en fazla 10MB olabilir', (file) =>
            file ? (file as File).size <= CV_MAX_SIZE : true
          ),
        answers: buildAnswerSchema(job.questionList),
        consentTerms: Yup.boolean().oneOf([true], 'Zorunlu'),
        consentPrivacy: Yup.boolean().oneOf([true], 'Zorunlu'),
      }),
    [job.questionList]
  )

  const formik = useFormik<IApplyFormValues>({
    initialValues: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      candidateLocation: '',
      cv: null,
      answers: {},
      consentTerms: false,
      consentPrivacy: false,
      consentFutureContact: false,
    },
    validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitError(null)
      try {
        const cv = values.cv as File
        let documentId = uploadedCvRef.current?.file === cv ? uploadedCvRef.current.documentId : null
        if (!documentId) {
          documentId = (await uploadCv(job.id, cv, captchaToken)).data.payload.id
          uploadedCvRef.current = {file: cv, documentId}
        }

        await applyJob({
          jobId: job.id,
          firstname: values.firstname.trim(),
          lastname: values.lastname.trim(),
          email: values.email.trim(),
          phone: `${PHONE_PREFIX} ${values.phone.replace(/\s/g, '')}`,
          candidateLocation: values.candidateLocation.trim(),
          documentId,
          consentTerms: values.consentTerms,
          consentPrivacy: values.consentPrivacy,
          consentFutureContact: values.consentFutureContact,
          questions: Object.entries(values.answers)
            .map(([questionId, value]) => ({questionId, value: value.trim()}))
            .filter((answer) => answer.value.length > 0),
        }, captchaToken)
        onSuccess()
      } catch (error) {
        setSubmitError(apiErrorMessage(error, 'Başvurunuz gönderilemedi. Lütfen tekrar deneyiniz.'))
        // Tekrar başvuruda (409) sunucu yüklenen CV'yi siler; yeni denemede yeniden yüklenir
        if ((error as any)?.response?.status === 409) uploadedCvRef.current = null
        // Captcha token'ı kullanılmış olabilir; tekrar denemek için yenisi alınır
        turnstileRef.current?.reset()
      } finally {
        setSubmitting(false)
      }
    },
  })

  const answerErrors = (formik.errors.answers || {}) as FormikErrors<Record<string, string>>
  const answerTouched = (formik.touched.answers || {}) as FormikTouched<Record<string, string>>

  const fieldError = (field: keyof IApplyFormValues) =>
    formik.touched[field] && formik.errors[field] ? (formik.errors[field] as string) : undefined

  const answerError = (questionId: string) =>
    (answerTouched[questionId] || formik.submitCount > 0) && answerErrors[questionId]
      ? answerErrors[questionId]
      : undefined

  const setAnswer = (questionId: string, value: string) => {
    formik.setFieldValue(`answers.${questionId}`, value)
    formik.setFieldTouched(`answers.${questionId}`, true, false)
  }

  const selectCv = (file?: File | null) => {
    if (!file) return
    formik.setFieldValue('cv', file)
    formik.setFieldTouched('cv', true, false)
  }

  const renderLabel = (text: string, required: boolean) => (
    <label className='pc-label'>
      {text}
      {required && <span className='pc-required'>*</span>}
    </label>
  )

  const renderTextField = (
    field: 'firstname' | 'lastname' | 'email' | 'candidateLocation',
    label: string,
    placeholder: string,
    icon?: string,
    type = 'text'
  ) => (
    <div className='pc-field'>
      {renderLabel(label, true)}
      <div className='pc-input-wrap'>
        {icon && <i className={`bi ${icon}`}></i>}
        <input
          type={type}
          className={`pc-input ${fieldError(field) ? 'is-invalid' : ''}`}
          placeholder={placeholder}
          {...formik.getFieldProps(field)}
        />
      </div>
      {fieldError(field) && <div className='pc-error'>{fieldError(field)}</div>}
    </div>
  )

  const renderQuestion = (question: IQuestionModel) => {
    const questionId = question.id as string
    const value = formik.values.answers[questionId] ?? ''
    const error = answerError(questionId)
    const inputClass = `pc-input ${error ? 'is-invalid' : ''}`
    const onBlur = () => formik.setFieldTouched(`answers.${questionId}`, true)

    let input: React.ReactNode
    switch (question.inputType) {
      case 'TEXTAREA':
        input = (
          <textarea
            className={inputClass}
            value={value}
            onChange={(event) => setAnswer(questionId, event.target.value)}
            onBlur={onBlur}
          />
        )
        break
      case 'NUMBER':
        input = (
          <input
            type='text'
            inputMode='decimal'
            className={inputClass}
            value={value}
            onChange={(event) => setAnswer(questionId, event.target.value)}
            onBlur={onBlur}
          />
        )
        break
      case 'DATE':
        input = (
          <input
            type='date'
            className={inputClass}
            value={value}
            onChange={(event) => setAnswer(questionId, event.target.value)}
            onBlur={onBlur}
          />
        )
        break
      case 'SELECT':
        input = (
          <select
            className={inputClass}
            value={value}
            onChange={(event) => setAnswer(questionId, event.target.value)}
            onBlur={onBlur}
          >
            <option value=''>{question.label}</option>
            {question.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )
        break
      case 'RADIO':
      case 'BOOLEAN': {
        const choices =
          question.inputType === 'BOOLEAN'
            ? [
                {value: 'true', label: 'Evet'},
                {value: 'false', label: 'Hayır'},
              ]
            : question.options.map((option) => ({value: option, label: option}))
        input = (
          <div className={question.inputType === 'BOOLEAN' ? 'pc-choice-row' : ''}>
            {choices.map((choice) => (
              <label key={choice.value} className='pc-choice'>
                <input
                  type='radio'
                  name={`question-${questionId}`}
                  checked={value === choice.value}
                  onChange={() => setAnswer(questionId, choice.value)}
                />
                {choice.label}
              </label>
            ))}
          </div>
        )
        break
      }
      default:
        input = (
          <input
            type='text'
            className={inputClass}
            value={value}
            onChange={(event) => setAnswer(questionId, event.target.value)}
            onBlur={onBlur}
          />
        )
    }

    return (
      <div key={questionId} className='pc-field'>
        {renderLabel(question.label, question.required)}
        {input}
        {error && <div className='pc-error'>{error}</div>}
      </div>
    )
  }

  const renderConsent = (
    field: 'consentTerms' | 'consentPrivacy' | 'consentFutureContact',
    content: React.ReactNode,
    required: boolean
  ) => (
    <label className='pc-consent'>
      <input
        type='checkbox'
        checked={formik.values[field]}
        onChange={(event) => formik.setFieldValue(field, event.target.checked)}
      />
      <span>
        {content}
        {required && <span className='pc-required text-warning ms-1'>*</span>}
      </span>
    </label>
  )

  const linkOrText = (url: string | null | undefined, text: string) => {
    const safeUrl = safeExternalUrl(url)
    return safeUrl ? (
      <a href={safeUrl} target='_blank' rel='noopener noreferrer'>
        {text}
      </a>
    ) : (
      text
    )
  }

  const cvError = fieldError('cv') || (formik.submitCount > 0 && formik.errors.cv)
  const consentsAccepted = formik.values.consentTerms && formik.values.consentPrivacy
  const captchaReady = !captchaRequired || !!captchaToken

  return (
    <form className='pc-aside' onSubmit={formik.handleSubmit} noValidate>
      <div className='pc-aside-title'>Başvuru</div>
      <div className='pc-aside-text'>
        Bu pozisyona başvurmak için formu doldurun ve işe alım sürecinizi başlatın.
      </div>

      {renderTextField('firstname', 'Ad', 'Adınız', 'bi-person-circle')}
      {renderTextField('lastname', 'Soyad', 'Soyadınız', 'bi-person-circle')}
      {renderTextField('email', 'E-posta', 'E-posta adresiniz', 'bi-envelope', 'email')}

      <div className='pc-field'>
        {renderLabel('Telefon', true)}
        <div className={`pc-phone ${fieldError('phone') ? 'is-invalid' : ''}`}>
          <span className='pc-phone-prefix'>
            <span role='img' aria-label='Türkiye'>
              🇹🇷
            </span>
            {PHONE_PREFIX}
          </span>
          <input
            type='tel'
            inputMode='tel'
            placeholder='5XX XXX XX XX'
            {...formik.getFieldProps('phone')}
          />
        </div>
        {fieldError('phone') && <div className='pc-error'>{fieldError('phone')}</div>}
      </div>

      {renderTextField('candidateLocation', 'Yaşadığınız Yer', 'Örn: İstanbul - Türkiye')}

      <div className='pc-field'>
        {renderLabel('CV / Özgeçmiş', true)}
        <div
          className={`pc-dropzone ${dragging ? 'is-dragging' : ''} ${cvError ? 'is-invalid' : ''}`}
          role='button'
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(event) => event.key === 'Enter' && fileInputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            selectCv(event.dataTransfer.files?.[0])
          }}
        >
          {formik.values.cv ? (
            <div className='d-flex align-items-center justify-content-center gap-3'>
              <i className='bi bi-file-earmark-check fs-2 text-success'></i>
              <div className='text-start'>
                <div className='fw-semibold text-gray-800'>{formik.values.cv.name}</div>
                <div className='text-muted fs-7'>{formatSize(formik.values.cv.size)} · Değiştirmek için tıklayın</div>
              </div>
            </div>
          ) : (
            <>
              <div className='pc-dropzone-icon'>
                <i className='bi bi-cloud-arrow-up'></i>
              </div>
              <div className='fs-5'>
                <span className='pc-dropzone-link'>Yüklemek için tıklayın</span> ya da dosyayı sürükleyip bırakın
              </div>
              <div className='text-muted fs-7 mt-2'>PDF, PNG, JPG/JPEG, DOC, DOCX (en fazla 10MB)</div>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type='file'
          className='d-none'
          accept={CV_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
          onChange={(event) => {
            selectCv(event.target.files?.[0])
            event.target.value = ''
          }}
        />
        {cvError && <div className='pc-error'>{cvError as string}</div>}
      </div>

      {job.questionList.length > 0 && (
        <>
          <div className='fs-4 fw-semibold text-gray-800 mt-10 mb-6'>
            Sizi daha yakından tanımak için birkaç soru.
          </div>
          {job.questionList.map(renderQuestion)}
        </>
      )}

      <div className='mt-8 mb-6'>
        {renderConsent('consentTerms', <>{linkOrText(company.termsUrl, 'Kullanım koşullarını')} okudum ve kabul ediyorum</>, true)}
        {renderConsent('consentPrivacy', <>{linkOrText(company.privacyUrl, 'KVKK aydınlatma metnini')} okudum</>, true)}
        {renderConsent(
          'consentFutureContact',
          `${company.name} ileride uygun pozisyonlar için 1 yıl boyunca benimle iletişime geçebilir.`,
          false
        )}
      </div>

      <Turnstile ref={turnstileRef} onToken={setCaptchaToken} />

      {submitError && <div className='alert alert-danger fs-6'>{submitError}</div>}

      <button type='submit' className='pc-btn-primary' disabled={!consentsAccepted || !captchaReady || formik.isSubmitting}>
        {formik.isSubmitting ? (
          <span className='spinner-border spinner-border-sm' />
        ) : (
          'Başvur'
        )}
      </button>
      {formik.submitCount > 0 && !formik.isValid && (
        <div className='pc-error text-center mt-3'>Lütfen işaretli alanları kontrol ediniz.</div>
      )}
    </form>
  )
}
